/* eslint-disable jsx-a11y/no-autofocus */
// TODO try to re-enable no-autofocus rule after closer examination
import React, { useEffect, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraSpinner,
  ChakraText,
  ChatSend,
  ChatUser,
  EquipUIFireflyV2Theme,
  EquipUITheme,
} from '@equip.health/ui';
import { Mention, MentionsInput } from 'react-mentions';

import { MessageTypeFilter } from '@sendbird/chat/message';

import {
  ChannelId,
  DEFAULT_ELEMENT_HEIGHT,
  DEFAULT_REPLY_PLACEHOLDER,
  DEFAULT_SUGGESTIONS_TITLE,
  ENTER_KEY,
  HERE_MEMBER,
  NEW_LINE,
} from '~/lib/constants/chat.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useMessageDraftsContext } from '~/lib/contexts/MessageDraftsContext';
import {
  ChatMessage,
  ChatMessageProps,
  FileAttachment,
  MentionsBoxUser,
} from '~/lib/types/chat';
import { getAllowedUsersForTagging } from '~/lib/util/chat/chat.utils';

import ChatAttachments from './ChatAttachments';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { MessagesEventsProps } from '~/analytics/messagesEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import AnalyticsEvents from '~/analytics';

const idPrefix = 'mentions';
const idPostfix = 'box';

const { caption } = EquipUITheme.typography;
const { neutral, primary } = EquipUIFireflyV2Theme.colors;

const defaultStyle = {
  '&multiLine': {
    control: {},
    highlighter: {
      border: 'none',
      padding: 9,
    },
    input: {
      backgroundColor: 'transparent',
      borderRadius: '10px',
      padding: 9,
    },
  },

  '&singleLine': {
    display: 'inline-block',

    highlighter: {
      padding: 1,
    },
    input: {
      backgroundColor: 'green',
      border: '2px inset',
      padding: 1,
    },

    width: 180,
  },

  control: {
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: '700',
    minHeight: DEFAULT_ELEMENT_HEIGHT,
    outline: 'none',
  },

  suggestions: {
    item: {
      '&focused': {
        backgroundColor: '#EFA53A1A',
      },

      borderBottom: '1px solid rgba(0,0,0,0.15)',
      padding: '5px 15px',
    },
    list: {
      backgroundColor: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      fontSize: 14,
    },
  },
};

const defaultMentionStyle = {
  backgroundColor: '#EFA53A1A',
};

const SuggestionsTitle = ({ id, children, title }) => {
  return (
    <ChakraFlex
      direction="column"
      id={`${idPrefix}-suggestions-container__${id}__${idPostfix}`}
    >
      <ChakraBox
        id={`${idPrefix}-suggestions-container__${id}__${idPostfix}`}
        padding="5px"
      >
        <ChakraText id={`${idPrefix}-suggestion-title__${id}__${idPostfix}`}>
          {title}
        </ChakraText>
      </ChakraBox>
      {children}
    </ChakraFlex>
  );
};

interface MentionsBoxProps {
  autofocus?: boolean;
  channelUrl: string;
  hideSuggestions?: boolean;
  id: string;
  isMessageSent: boolean;
  members: ChatUser[];
  onBlur?: VoidFunction;
  onEnterPressed?: (message: ChatMessage) => void;
  onFocus?: (ref: React.MutableRefObject<HTMLInputElement>) => void;
  onFileSelected?: (file?: FileAttachment) => void;
  placeholder?: string;
  suggestionsBelowCursor?: boolean;
  title?: string;
  updatedAttachment: FileAttachment;
}

const MentionsBox = React.forwardRef<HTMLInputElement, MentionsBoxProps>(
  (
    {
      autofocus = false,
      channelUrl,
      hideSuggestions = false,
      id,
      isMessageSent,
      members,
      onBlur,
      onEnterPressed,
      onFocus,
      onFileSelected,
      placeholder = DEFAULT_REPLY_PLACEHOLDER,
      suggestionsBelowCursor = false,
      title = DEFAULT_SUGGESTIONS_TITLE,
      updatedAttachment,
    }: MentionsBoxProps,
    ref?: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const { currentUser } = useGlobalContext();
    const users = getAllowedUsersForTagging(members, currentUser.userId);
    const { trackEvent } = useAnalytics();
    const inputTextRef = useRef<HTMLInputElement>(null);
    const [fileSizeErrorMessage, setFileSizeErrorMessage] =
      useState<string>(null);

    users.push({ display: HERE_MEMBER.nickname, id: HERE_MEMBER.userId });

    const [mentionedUsers, setMentionedUsers] = useState<MentionsBoxUser[]>([]);
    const [selectedFile, setSelectedFile] =
      useState<FileAttachment>(updatedAttachment);
    const { messageDrafts, onMessageDraftsChange } = useMessageDraftsContext();
    const inputValueRef = useRef<string>();
    inputValueRef.current = messageDrafts?.[channelUrl]?.[id] ?? '';

    const formatInputValue = (inputValue: string): string => {
      const isEmptyMessage = inputValue.trim().replaceAll(NEW_LINE, '') === '';
      let outputValue = inputValue.trim();
      if (!isEmptyMessage) {
        for (const mentionedUser of mentionedUsers) {
          outputValue = outputValue.replace(`(${mentionedUser.id})`, '');
        }
      }

      return isEmptyMessage ? null : outputValue;
    };

    const triggerOnEnterPressed = () => {
      const value = messageDrafts?.[channelUrl]?.[id] ?? '';
      if (value.trim() !== '' || selectedFile) {
        const getPatientIdFromChannelUrl = (channelUrl: string) => {
          const split = channelUrl.split(/[_]+/g);
          if (channelUrl.startsWith(ChannelId.ACT)) return split[1];
          else if (channelUrl.startsWith(ChannelId.PT_PR_CCT)) return split[3];
          else return split[2];
        };

        const eventProps: MessagesEventsProps = {
          channelType: channelUrl.split('_')[0],
          channelUrl,
          messageType: selectedFile
            ? MessageTypeFilter.FILE
            : MessageTypeFilter.USER,
          patientId: getPatientIdFromChannelUrl(channelUrl),
          source: AnalyticsSourceValues.PATIENT_MESSAGES_TAB,
        };

        trackEvent(
          AnalyticsEvents.patientChartMessagesCreateNewMessage,
          eventProps,
        );

        const newMessage: ChatMessageProps = {
          file: selectedFile,
          isAdminMessage: false,
          mentionedUserIds: mentionedUsers.map((x) => x.id),
          message: formatInputValue(value.trim()),
          parentMessageId: 0,
        };
        onEnterPressed?.(newMessage as ChatMessage);
        setSelectedFile(null);
        onMessageDraftsChange(channelUrl, id, '');
        setMentionedUsers([]);
      }
    };

    useEffect(() => {
      const initialDraft = messageDrafts?.[channelUrl]?.[id] ?? '';
      onMessageDraftsChange(channelUrl, id, initialDraft);
    }, [channelUrl]);

    useEffect(() => {
      setSelectedFile(updatedAttachment);
    }, [updatedAttachment]);

    return (
      <ChakraFlex alignItems="center" direction="column" width="100%">
        {fileSizeErrorMessage && (
          <ChakraText color="red" {...caption}>
            {fileSizeErrorMessage}
          </ChakraText>
        )}
        <ChakraFlex
          alignItems="center"
          backgroundColor="neutral.background.secondary"
          borderRadius="10px"
          id={`${idPrefix}-mentions-container__${id}__${idPostfix}`}
          width="100%"
        >
          <ChakraBox
            id={`${idPrefix}-mentions-input-container__${id}__${idPostfix}`}
            width="100%"
          >
            <MentionsInput
              autoFocus={autofocus}
              className="mentionsInput"
              customSuggestionsContainer={(children) => (
                <SuggestionsTitle id={id} title={title}>
                  {children}
                </SuggestionsTitle>
              )}
              disabled={isMessageSent}
              forceSuggestionsAboveCursor={!suggestionsBelowCursor}
              id={`${idPrefix}-mentions-input__${id}__${idPostfix}`}
              inputRef={inputTextRef}
              onBlur={(_, clickedSuggestion) => {
                if (!clickedSuggestion) {
                  onBlur?.();
                }
              }}
              onChange={(_, newValue, __, mentions) => {
                onMessageDraftsChange(channelUrl, id, newValue);
                setMentionedUsers(Array.from(mentions));
              }}
              onFocus={() => onFocus?.(inputTextRef)}
              onKeyDown={(e) => {
                if (e.key === ENTER_KEY && !e.shiftKey && !e.ctrlKey) {
                  triggerOnEnterPressed();
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              placeholder={placeholder}
              ref={ref}
              style={defaultStyle}
              value={messageDrafts?.[channelUrl]?.[id] ?? ''}
            >
              <Mention
                appendSpaceOnAdd
                data={hideSuggestions ? null : users}
                displayTransform={(id, display) => `@${display}`}
                id={`${idPrefix}-mentions-trigger__${id}__${idPostfix}`}
                markup="@__display__(__id__)"
                style={defaultMentionStyle}
                trigger="@"
              />
            </MentionsInput>
          </ChakraBox>

          <ChatAttachments
            id={id}
            isMessageSent={isMessageSent}
            onFileSelected={onFileSelected}
            onFileSizeError={setFileSizeErrorMessage}
          />
          <ChakraBox
            cursor="pointer"
            id={`${idPrefix}-mentions-send-container__${id}__${idPostfix}`}
            onClick={triggerOnEnterPressed}
            padding="10px"
          >
            {isMessageSent ? (
              <ChakraSpinner color={neutral.tertiary} size="sm" />
            ) : (
              <ChatSend
                fill={
                  (messageDrafts?.[channelUrl]?.[id] ?? '').trim() !== '' ||
                  selectedFile
                    ? primary[100]
                    : neutral.tertiary
                }
                id={`${idPrefix}-mentions-send__${id}__${idPostfix}`}
              />
            )}
          </ChakraBox>
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);

MentionsBox.displayName = 'MentionsBox';

export default MentionsBox;
